import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useWallet } from '../../context/WalletContext';
import { useContract } from '../../context/ContractContext';
import { useNotification } from '../../context/NotificationContext';
import PageContainer from '../../components/layout/PageContainer/PageContainer';
import PackageSelector from '../../components/invest/PackageSelector/PackageSelector';
import InvestmentCard from '../../components/invest/InvestmentCard/InvestmentCard';
import Button from '../../components/common/Button/Button';
import { formatNumber } from '../../utils/formatters';
import './Invest.css';

const Invest = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isConnected, isInitializing } = useWallet();
  const { 
    isRegistered,
    isCheckingRegistration,
    userId,
    tokenBalance, 
    tokenAllowance,
    approveTokens,
    invest,
    getUserInfo,
    getAllInvestments,
    getTimeUnit,
    isLoading 
  } = useContract();
  const { error: showError, success: showSuccess, info: showInfo } = useNotification();

  const [selectedPackage, setSelectedPackage] = useState('');
  const [currentPackage, setCurrentPackage] = useState('0');
  const [investments, setInvestments] = useState([]);
  const [loadingInvestments, setLoadingInvestments] = useState(true);
  const [activeTab, setActiveTab] = useState('invest');
  const [processing, setProcessing] = useState(false);
  const [timeUnit, setTimeUnit] = useState(300); // default 5 min

  useEffect(() => {
    if (isInitializing || isCheckingRegistration) return;
    
    if (!isConnected) {
      navigate('/');
      return;
    }
    if (!isRegistered) {
      navigate('/register');
      return;
    }
  }, [isConnected, isRegistered, isInitializing, isCheckingRegistration, navigate]);

  useEffect(() => {
    // Check for tab in URL
    const params = new URLSearchParams(location.search);
    if (params.get('tab') === 'my') {
      setActiveTab('history');
    }
  }, [location]);

  useEffect(() => {
    const fetchData = async () => {
      if (!userId) return;
      
      try {
        const [userInfo, invs, unit] = await Promise.all([
          getUserInfo(),
          getAllInvestments(),
          getTimeUnit ? getTimeUnit() : Promise.resolve(300)
        ]);
        
        setCurrentPackage(userInfo?.currentPackage || '0');
        setInvestments(invs);
        setTimeUnit(unit || 300);
      } catch (err) {
        console.error('Error fetching data:', err);
      }
      setLoadingInvestments(false);
    };

    fetchData();
  }, [userId]);

  const handleInvest = async () => {
    if (!selectedPackage) {
      showError('Please select a package');
      return;
    }

    const hasBalance = parseFloat(tokenBalance) >= parseFloat(selectedPackage);
    if (!hasBalance) {
      showError('Insufficient USDT balance');
      return;
    }

    setProcessing(true);

    try {
      // Check if approval is needed
      const needsApproval = parseFloat(tokenAllowance) < parseFloat(selectedPackage);
      
      if (needsApproval) {
        showInfo('Step 1/2: Approving USDT...');
        const approveSuccess = await approveTokens(selectedPackage);
        if (!approveSuccess) {
          setProcessing(false);
          return;
        }
        showSuccess('Approval successful! Investing...');
      }

      // Now invest
      showInfo(needsApproval ? 'Step 2/2: Investing...' : 'Investing...');
      const success = await invest(selectedPackage);
      
      if (success) {
        // Refresh data
        const [userInfo, invs] = await Promise.all([
          getUserInfo(),
          getAllInvestments()
        ]);
        setCurrentPackage(userInfo?.currentPackage || '0');
        setInvestments(invs);
        setSelectedPackage('');
        setActiveTab('history');
      }
    } catch (err) {
      showError('Investment failed');
    } finally {
      setProcessing(false);
    }
  };

  const hasBalance = parseFloat(tokenBalance) >= parseFloat(selectedPackage || 0);

  return (
    <PageContainer title="Invest">
      <div className="invest-page">
        <div className="invest-tabs">
          <button 
            className={`invest-tab ${activeTab === 'invest' ? 'active' : ''}`}
            onClick={() => setActiveTab('invest')}
          >
            New Investment
          </button>
          <button 
            className={`invest-tab ${activeTab === 'history' ? 'active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            My Investments
          </button>
        </div>

        {activeTab === 'invest' ? (
          <div className="invest-content">
            <div className="invest-balance">
              <div className="balance-item">
                <span className="balance-label">USDT Balance</span>
                <span className="balance-value">{formatNumber(tokenBalance)}</span>
              </div>
              <div className="balance-item">
                <span className="balance-label">Current Package</span>
                <span className="balance-value highlight">{formatNumber(currentPackage, 0)}</span>
              </div>
            </div>

            <div className="invest-section">
              <h3 className="section-title">Select Package</h3>
              <p className="section-hint">Package must be equal or higher than current</p>
              <PackageSelector
                selected={selectedPackage}
                onSelect={setSelectedPackage}
                currentPackage={currentPackage}
              />
            </div>

            {selectedPackage && (
              <div className="invest-summary">
                <div className="summary-row">
                  <span>Investment Amount</span>
                  <span className="summary-value">{selectedPackage} USDT</span>
                </div>
                <div className="summary-row">
                  <span>300% Cap</span>
                  <span className="summary-value">{parseFloat(selectedPackage) * 3} USDT</span>
                </div>
              </div>
            )}

            <div className="invest-actions">
              <Button
                fullWidth
                size="lg"
                onClick={handleInvest}
                loading={isLoading || processing}
                disabled={!selectedPackage || !hasBalance || processing}
              >
                {processing ? 'Processing...' : 'Invest Now'}
              </Button>

              {!hasBalance && selectedPackage && (
                <p className="invest-error">Insufficient USDT balance</p>
              )}
            </div>
          </div>
        ) : (
          <div className="investments-list">
            {loadingInvestments ? (
              <div className="investments-loading">Loading investments...</div>
            ) : investments.length === 0 ? (
              <div className="investments-empty">
                <p>No investments yet</p>
                <Button onClick={() => setActiveTab('invest')}>Make Your First Investment</Button>
              </div>
            ) : (
              investments.map((inv, idx) => (
                <InvestmentCard 
                  key={idx} 
                  investment={inv} 
                  index={idx} 
                  timeUnit={timeUnit}
                />
              ))
            )}
          </div>
        )}
      </div>
    </PageContainer>
  );
};

export default Invest;